import React, { useState, useEffect, useRef } from 'react';
import { X, QrCode } from 'lucide-react';
import DeliverySelect from './DeliverySelect';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useTheme } from '../ThemeContext';

const EditSalesforceModal = ({ isOpen, onClose, service, onUpdate, deliveries, logAction }) => {
    const colorOptions = [
        {
            name: 'Bleu',
            color: 'from-blue-500 to-blue-600',
            gradient: 'bg-gradient-to-br from-blue-500/80 to-blue-600/90'
        },
        {
            name: 'Rouge',
            color: 'from-red-500 to-red-600',
            gradient: 'bg-gradient-to-br from-red-500/80 to-red-600/90'
        },
        {
            name: 'Vert',
            color: 'from-emerald-500 to-emerald-600',
            gradient: 'bg-gradient-to-br from-emerald-500/80 to-emerald-600/90'
        },
        {
            name: 'Violet',
            color: 'from-purple-500 to-purple-600',
            gradient: 'bg-gradient-to-br from-purple-500/80 to-purple-600/90'
        },
        {
            name: 'Orange',
            color: 'from-orange-500 to-orange-600',
            gradient: 'bg-gradient-to-br from-orange-500/80 to-orange-600/90'
        }
    ];

    const [formData, setFormData] = useState({
        name: '',
        username: '',
        description: '',
        secret: '',
        delivery: '',
        color: '',
        gradient: ''
    });

    const [error, setError] = useState('');
    const [showSecretConfirm, setShowSecretConfirm] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [originalData, setOriginalData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false); // 🔥 NOUVEAU: Prévention double submit
    const { isDark } = useTheme();
    const submitTimeoutRef = useRef(null); // 🔥 NOUVEAU: Ref pour gérer le timeout

    useEffect(() => {
        if (service) {
            const initialData = {
                name: service.name || '',
                username: service.username || '',
                description: service.description || '',
                secret: service.secret || '',
                delivery: service.delivery || '',
                color: service.color || 'from-blue-500 to-blue-600',
                gradient: service.gradient || 'bg-gradient-to-br from-blue-500/80 to-blue-600/90'
            };
            setFormData(initialData);
            setOriginalData(initialData);
            setError('');
            setShowSecretConfirm(false);
            setIsSubmitting(false); // 🔥 NOUVEAU: Reset du statut
        }
    }, [service]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });

        if (e.target.name === 'secret') {
            setShowSecretConfirm(false);
        }
    };

    // 🔥 NOUVEAU: Fonction pour vérifier si des changements ont été faits
    const hasChanges = () => {
        return Object.keys(formData).some(key => formData[key] !== originalData[key]);
    };

    const handleSubmit = async () => {
        // 🔥 NOUVEAU: Prévention double submit
        if (isSubmitting) {
            console.log('⚠️ Soumission déjà en cours, ignorée');
            return;
        }

        // 🔥 NOUVEAU: Vérifier s'il y a des changements
        if (!hasChanges()) {
            console.log('ℹ️ Aucun changement détecté');
            onClose();
            return;
        }

        try {
            if (formData.secret !== originalData.secret && !showSecretConfirm) {
                setShowSecretConfirm(true);
                return;
            }

            setIsSubmitting(true); // 🔥 NOUVEAU: Marquer comme en cours de soumission

            // Identifier les champs modifiés
            const changedFields = [];
            const previousValues = {};
            const newValues = {};

            Object.keys(formData).forEach(key => {
                if (formData[key] !== originalData[key]) {
                    changedFields.push(key);
                    previousValues[key] = originalData[key];
                    newValues[key] = formData[key];
                }
            });

            const updatedFields = {
                name: formData.name,
                username: formData.username,
                description: formData.description,
                secret: formData.secret,
                delivery: formData.delivery,
                color: formData.color,
                gradient: formData.gradient,
                // Champs spécifiques Salesforce
                sf_username: formData.username,
                sf_description: formData.description
            };

            const response = await fetch(`/api/salesforce/${service.id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatedFields)
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la mise à jour');
            }

            // 🔥 OPTIMISÉ: Logger seulement une fois avec un délai
            if (logAction && changedFields.length > 0) {
                // Clear any existing timeout
                if (submitTimeoutRef.current) {
                    clearTimeout(submitTimeoutRef.current);
                }

                // Set a new timeout to log after a delay
                submitTimeoutRef.current = setTimeout(() => {
                    logAction('SERVICE_UPDATED', {
                        serviceName: formData.name,
                        serviceId: service.id,
                        changedFields: changedFields,
                        previousValues: previousValues,
                        newValues: newValues
                    });
                }, 500); // 500ms delay
            }

            // Si le statut est 204, pas besoin de parser la réponse
            if (response.status === 204) {
                onUpdate(); // Déclencher le refresh sans paramètre
                onClose();
                return;
            }

            // Sinon tenter de parser la réponse (si elle existe)
            try {
                const updatedService = await response.json();
                onUpdate(updatedService);
            } catch {
                // Même si on ne peut pas parser la réponse, l'update a réussi
                onUpdate();
            }
            onClose();
        } catch (error) {
            console.error('Erreur de mise à jour:', error);
            setError('Erreur lors de la mise à jour du service.');
        } finally {
            setIsSubmitting(false); // 🔥 NOUVEAU: Reset du statut
        }
    };

    // 🔥 NOUVEAU: Cleanup du timeout
    useEffect(() => {
        return () => {
            if (submitTimeoutRef.current) {
                clearTimeout(submitTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (showScanner) {
            const scanner = new Html5QrcodeScanner("reader", {
                qrbox: {
                    width: 250,
                    height: 250,
                },
                fps: 5,
                rememberLastUsedCamera: true,
                aspectRatio: 1.0,
                showTorchButtonIfSupported: true,
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                text: {
                    camera: "Caméra",
                    chooseImage: "Choisir une image",
                    drag: "ou déposez une image ici",
                    file: "Parcourir",
                    scan: "Scanner",
                    scanning: "Scan en cours...",
                    torchOff: "Éteindre le flash",
                    torchOn: "Allumer le flash",
                    uploadImage: "Télécharger une image",
                    requestingCamera: "Demande d'accès à la caméra...",
                    scanningInProgress: "Scan en cours...",
                    permissionDenied: "Permission refusée",
                    permissionRequest: "Demande de permission",
                    noCamera: "Aucune caméra trouvée",
                    retry: "Réessayer",
                    scanImage: "Scanner une image",
                    scanAnImageFile: "Scanner à partir d'un fichier",
                    requestCameraPermissions: "Autoriser l'accès à la caméra",
                    chooseAnImage: "Choisir une image",
                    noImageChosen: "Aucune image sélectionnée",
                    dropImageHere: "Déposez une image ici",
                    scanUsingCamera: "Scanner avec la caméra",
                    selectOrDropImage: "Sélectionnez ou déposez une image",
                    cameraPermissionRequesting: "Demande d'accès à la caméra..."
                },
                buttonClassName: "px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200",
                containerClassName: "rounded-xl overflow-hidden",
                fileSelectionUiClassName: "space-y-4"
            });

            scanner.render(
                (decodedText) => {
                    try {
                        // Si c'est une URL otpauth:// (Google, Microsoft, etc.)
                        if (decodedText.startsWith('otpauth://')) {
                            const url = new URL(decodedText);
                            const secret = url.searchParams.get('secret');
                            if (secret) {
                                setFormData(prev => ({
                                    ...prev,
                                    secret: secret
                                }));
                                scanner.clear();
                                setShowScanner(false);
                                return;
                            }
                        }

                        // Si c'est déjà une clé secrète brute (certains services)
                        if (/^[A-Z2-7]+=*$/i.test(decodedText)) {
                            setFormData(prev => ({
                                ...prev,
                                secret: decodedText
                            }));
                            scanner.clear();
                            setShowScanner(false);
                            return;
                        }

                        // Si c'est au format JSON (certains services personnalisés)
                        try {
                            const jsonData = JSON.parse(decodedText);
                            if (jsonData.secret) {
                                setFormData(prev => ({
                                    ...prev,
                                    secret: jsonData.secret
                                }));
                                scanner.clear();
                                setShowScanner(false);
                                return;
                            }
                        } catch (e) {
                            // Ce n'est pas du JSON, on continue
                        }

                        // Si on arrive ici, le format n'est pas reconnu
                        setError('Format de QR code non supporté');
                        scanner.clear();
                        setShowScanner(false);

                    } catch (error) {
                        console.error('Erreur de parsing du QR code:', error);
                        setError('Format de QR code non reconnu');
                        scanner.clear();
                        setShowScanner(false);
                    }
                },
                (error) => {
                    console.warn(error);
                }
            );

            return () => {
                scanner.clear();
            };
        }
    }, [showScanner]);

    const renderScanner = () => (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl max-w-md w-full m-4 relative space-y-4">
                <button
                    onClick={() => setShowScanner(false)}
                    className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                    <X size={20} />
                </button>

                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Scanner un QR Code</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        Positionnez le QR code devant votre caméra ou sélectionnez une image
                    </p>
                </div>

                <div id="reader" className="overflow-hidden rounded-xl shadow-inner bg-gray-50">
                    {/* Le scanner sera injecté ici */}
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center md:p-4">
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl md:rounded-2xl p-6 w-full max-w-md space-y-6 md:h-auto h-screen md:max-h-[85vh] overflow-y-auto">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                    >
                        <X size={20} />
                    </button>

                    <div className="text-center pt-2">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                            Modifier {formData.name || 'Service'}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            Modifiez les informations du service
                        </p>
                        {/* 🔥 NOUVEAU: Indicateur de changements */}
                        {hasChanges() && (
                            <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs">
                                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                Modifications non sauvegardées
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nom du service
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nom d'utilisateur
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                            </label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Couleur de la carte
                            </label>
                            <div className="grid grid-cols-5 gap-2">
                                {colorOptions.map((option) => {
                                    const isSelected = formData.color === option.color;
                                    return (
                                        <button
                                            key={option.name}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                color: option.color,
                                                gradient: option.gradient
                                            }))}
                                            className={`p-4 rounded-xl h-14 ${option.gradient} transition-all duration-200 
                                                ${isSelected
                                                    ? 'ring-2 ring-offset-2 ring-blue-400 scale-105'
                                                    : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-200'}`
                                            }
                                            type="button"
                                            title={option.name}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <DeliverySelect
                                value={formData.delivery}
                                onChange={handleChange}
                                deliveries={deliveries}
                            />
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Clé secrète
                            </label>
                            <input
                                type="text"
                                name="secret"
                                value={formData.secret}
                                onChange={handleChange}
                                className="w-full px-4 py-3 pr-12 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                            <button
                                onClick={() => setShowScanner(true)}
                                type="button"
                                className="absolute right-2 top-[65%] -translate-y-1/2 p-2 bg-blue-50 dark:bg-blue-400/10 hover:bg-blue-100 dark:hover:bg-blue-400/20 rounded-lg transition-colors"
                                title="Scanner un QR Code"
                            >
                                <QrCode size={20} className="text-blue-500" />
                            </button>
                        </div>

                        {showSecretConfirm && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg">
                                <p className="text-yellow-800 dark:text-yellow-500 font-medium">⚠️ Attention</p>
                                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                                    Vous êtes sur le point de modifier la clé secrète. Cette action est irréversible et
                                    pourrait affecter la génération des codes. Êtes-vous sûr de vouloir continuer ?
                                </p>
                                <div className="mt-3 flex gap-3">
                                    <button
                                        onClick={() => {
                                            setShowSecretConfirm(false);
                                            setFormData(prev => ({
                                                ...prev,
                                                secret: originalData.secret
                                            }));
                                        }}
                                        className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-yellow-300 dark:border-yellow-500/30 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                        className="px-3 py-1 text-sm bg-yellow-100 dark:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-500/30 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-900/70 text-yellow-700 dark:text-yellow-300 disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Confirmation...' : 'Confirmer'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="text-red-500 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 rounded-xl border hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white transition-colors duration-200"
                        >
                            Annuler
                        </button>
                        {!showSecretConfirm && (
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || !hasChanges()}
                                className="flex-1 px-4 py-3 rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {showScanner && renderScanner()}
        </div>
    );
};

export default EditSalesforceModal;